// client/src/components/agent/ConversationsView.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import ConversationList from './ConversationList';
import MessagePanel from './MessagePanel';
import ActionsPanel from './ActionsPanel';
import MessageHistoryModal from './MessageHistoryModal';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { alertIncoming, requestNotificationPermission, unlockAudio } from '../../utils/notify';

export default function ConversationsView({ token, socket }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contact, setContact] = useState(null);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [flows, setFlows] = useState([]);
  const [typifications, setTypifications] = useState([]);
  const [historyConv, setHistoryConv] = useState(null);
  const [company, setCompany] = useState(null);
  const [showActions, setShowActions] = useState(false);
  // Ancho del panel de acciones (derecha). El agente lo redimensiona arrastrando el
  // divisor. Se conserva entre conversaciones durante la sesión.
  const [actionsWidth, setActionsWidth] = useState(280);
  const dragRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: actionsWidth };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      // El panel está a la derecha: arrastrar hacia la izquierda lo ensancha.
      const delta = dragRef.current.startX - ev.clientX;
      const next = Math.min(640, Math.max(240, dragRef.current.startWidth + delta));
      setActionsWidth(next);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const isMobile = useIsMobile();
  // En móvil mostramos un solo panel a la vez:
  //  - 'list'    : aún no hay conversación seleccionada
  //  - 'actions' : el agente abrió el panel de acciones
  //  - 'chat'    : hay conversación seleccionada
  const mobilePane = !activeConv ? 'list' : (showActions ? 'actions' : 'chat');

  const headers = { Authorization: `Bearer ${token}` };

  // Pide permiso de notificaciones y desbloquea el audio al primer gesto del agente
  // (las políticas de autoplay exigen una interacción antes de poder sonar).
  useEffect(() => {
    requestNotificationPermission();
    const onGesture = () => { unlockAudio(); requestNotificationPermission(); };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, []);

  useEffect(() => {
    fetch('/api/agent/conversations', { headers }).then((r) => r.json()).then(setConversations).catch(() => {});
    fetch('/api/agent/typifications', { headers }).then((r) => r.json()).then(setTypifications).catch(() => {});
    fetch('/api/agent/online-agents', { headers }).then((r) => r.json()).then(setOnlineAgents).catch(() => {});
    fetch('/api/agent/flows', { headers }).then((r) => r.json()).then(setFlows).catch(() => {});
    fetch('/api/agent/company', { headers }).then((r) => r.json()).then(setCompany).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket) return;
    socket.onConversationAssigned = () => {
      fetch('/api/agent/conversations', { headers }).then((r) => r.json()).then(setConversations).catch(() => {});
    };
    socket.onConversationRemoved = ({ conversation_id }) => {
      setConversations((prev) => prev.filter((c) => c.id !== conversation_id));
      if (activeConv?.id === conversation_id) {
        setActiveConv(null);
        setMessages([]);
        setContact(null);
      }
    };
    socket.onNewMessage = ({ conversation_id, message }) => {
      if (activeConv?.id === conversation_id) {
        setMessages((prev) => [...prev, message]);
      }
      // Sonido + notificación en cada mensaje entrante (cualquier canal). No suena
      // con los mensajes que el propio agente envía (se reflejan también aquí).
      if (message && message.send !== 'agent') {
        const preview = message.type === 'text'
          ? (message.content || '')
          : `📎 ${message.type === 'audio' ? 'Nota de voz' : 'Archivo adjunto'}`;
        alertIncoming('Nuevo mensaje', preview, { tag: `conv-${conversation_id}` });
      }
      setConversations((prev) =>
        prev
          .map((c) => c.id === conversation_id ? { ...c, last_message: message.content, updated_at: message.created_at } : c)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      );
    };
    socket.onAgentStatusChanged = () => {
      fetch('/api/agent/online-agents', { headers }).then((r) => r.json()).then(setOnlineAgents).catch(() => {});
    };
  }, [socket, activeConv, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Red de seguridad: re-consulta los mensajes de la conversación activa cada 3s.
  // Los eventos de socket dan la actualización instantánea; este polling garantiza
  // que el agente vea SIEMPRE lo último (cliente, bot/flujo, admin) sin recargar,
  // aunque se pierda un evento. Espeja el comportamiento del visor del admin.
  useEffect(() => {
    if (!activeConv) return;
    const id = setInterval(() => {
      fetch(`/api/agent/conversations/${activeConv.id}/messages`, { headers })
        .then((r) => (r.ok ? r.json() : null))
        .then((msgs) => { if (Array.isArray(msgs)) setMessages(msgs); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [activeConv, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    setShowActions(false);
    try {
      const msgs = await fetch(`/api/agent/conversations/${conv.id}/messages`, { headers }).then((r) => r.json());
      setMessages(msgs);
    } catch { setMessages([]); }
    try {
      const contacts = await fetch('/api/agent/contacts', { headers }).then((r) => r.json());
      setContact(contacts.find((c) => c.id === conv.owner) || { id: conv.owner, custom: {}, list_contact: [] });
    } catch { setContact({ id: conv.owner, custom: {}, list_contact: [] }); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // En móvil cada panel decide si se muestra según mobilePane; en escritorio
  // siempre se muestran los tres (comportamiento original intacto).
  const showList = !isMobile || mobilePane === 'list';
  const showChat = !isMobile || mobilePane === 'chat';
  const showActionsPanel = !isMobile || mobilePane === 'actions';

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minWidth: 0 }}>
      {showList && (
        <ConversationList
          conversations={conversations}
          activeId={activeConv?.id}
          onSelect={selectConversation}
          onViewHistory={setHistoryConv}
          isMobile={isMobile}
        />
      )}
      {showChat && (
        <MessagePanel
          token={token}
          conv={activeConv}
          messages={messages}
          onSend={(payload) => socket?.sendMessage(payload)}
          onTransfer={(convId, toId) => socket?.transferConversation(convId, toId)}
          onTransferToFlow={(convId, flowId) => socket?.transferToFlow(convId, flowId)}
          onClose={(convId, typId, notas) => socket?.closeConversation(convId, typId, notas)}
          onlineAgents={onlineAgents}
          flows={flows}
          typifications={typifications}
          isMobile={isMobile}
          onBack={() => setActiveConv(null)}
          onOpenActions={() => setShowActions(true)}
        />
      )}
      {showActionsPanel && !isMobile && (
        <div
          onMouseDown={startResize}
          title="Arrastra para ajustar el ancho del panel"
          style={{ width: 6, flexShrink: 0, cursor: 'col-resize', background: 'transparent', borderLeft: '1px solid rgba(255,255,255,0.06)' }}
        />
      )}
      {showActionsPanel && (
        <ActionsPanel
          token={token}
          conv={activeConv}
          contact={contact}
          company={company}
          width={isMobile ? undefined : actionsWidth}
          onContactUpdated={(fields) =>
            setContact((prev) => prev ? { ...prev, custom: { ...(prev.custom || {}), ...fields } } : prev)
          }
          isMobile={isMobile}
          onBack={() => setShowActions(false)}
        />
      )}
      {historyConv && (
        <MessageHistoryModal conv={historyConv} token={token} onClose={() => setHistoryConv(null)} />
      )}
    </div>
  );
}
